const NMLS_CONSUMER_ACCESS_BASE = 'https://www.nmlsconsumeraccess.org/EntityDetails.aspx/COMPANY/'

type FooterConfig = Record<string, any>

const isPlainObject = (value: unknown): value is FooterConfig =>
  !!value && typeof value === 'object' && !Array.isArray(value)

/**
 * Company footer is the base; white-label / manage-account custom footer overlays it.
 * Sparse overrides (e.g. only `{ nmls }`) keep company phone, address, links, etc.
 * Empty override strings fall back to company so partial edits do not blank the footer.
 */
export const mergeFooterConfig = (
  company: FooterConfig | null | undefined,
  override: FooterConfig | null | undefined,
): FooterConfig => {
  const base = isPlainObject(company) ? { ...company } : {}
  if (!isPlainObject(override)) return base

  const result: FooterConfig = { ...base }

  Object.keys(override).forEach((key) => {
    const ov = override[key]
    if (ov === undefined || ov === null) return

    if (Array.isArray(ov)) {
      if (ov.length > 0) result[key] = ov
      return
    }

    if (!isPlainObject(ov)) {
      result[key] = ov
      return
    }

    const companyField = isPlainObject(base[key]) ? base[key] : {}
    const merged = { ...companyField, ...ov }

    if (Object.prototype.hasOwnProperty.call(ov, 'value')) {
      const raw = ov.value
      const empty = raw === '' || raw === null || raw === undefined
      if (empty && companyField.value !== undefined) merged.value = companyField.value
    }

    result[key] = merged
  })

  return result
}

/** Append Company NMLS from footer.nmls onto the default About Us NMLS link. */
export const resolveFooterLinks = (links: any[] | undefined, nmlsValue: string) => {
  if (!Array.isArray(links)) return []
  const value = (nmlsValue || '').trim()
  if (!value) return links

  return links.map((link: any) => {
    const label = String(link?.label || '').trim()
    const url = String(link?.url || '').trim()
    const isNmlsLink =
      /^Company NMLS:/i.test(label) ||
      /^https?:\/\/www\.nmlsconsumeraccess\.org\/EntityDetails\.aspx\/COMPANY\/?/i.test(url)
    if (!isNmlsLink) return link

    return {
      ...link,
      label: `Company NMLS: ${value}`,
      url: `${NMLS_CONSUMER_ACCESS_BASE}${value}`,
    }
  })
}
